import api from './api';

export interface GitLabInstance {
  id: string;
  name: string;
  url: string;
  authType: 'pat' | 'project-token';
  token: string; // Will be '***' from API
  defaultProjectId?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface GitLabProject {
  id: number;
  name: string;
  name_with_namespace: string;
  path_with_namespace: string;
  description: string | null;
  default_branch: string;
  web_url: string;
  last_activity_at: string;
}

export interface GitLabTreeItem {
  id: string;
  name: string;
  type: 'tree' | 'blob';
  path: string;
  mode: string;
}

export interface GitLabFileResult {
  content: string;
  fileMeta: {
    file_name: string;
    file_path: string;
    size: number;
    ref: string;
    last_commit_id: string;
  };
}

export interface GitLabBranch {
  name: string;
  default: boolean;
  merged: boolean;
  protected: boolean;
  web_url: string;
  commit: {
    id: string;
    short_id: string;
    title: string;
    author_name: string;
    authored_date: string;
  };
}

export interface GitLabPipeline {
  id: number;
  iid: number;
  status: string;
  ref: string;
  sha: string;
  web_url: string;
  created_at: string;
  updated_at: string;
  source: string;
}

export const gitlabApi = {
  // Instance CRUD
  listInstances: async (): Promise<GitLabInstance[]> => {
    const { data } = await api.get<GitLabInstance[]>('/gitlab/instances');
    return data;
  },

  addInstance: async (config: {
    name: string;
    url: string;
    authType: string;
    token: string;
    defaultProjectId?: number;
  }): Promise<GitLabInstance> => {
    const { data } = await api.post<{ success: boolean; data: GitLabInstance }>('/gitlab/instances', config);
    return data.data;
  },

  updateInstance: async (id: string, updates: Partial<GitLabInstance>): Promise<GitLabInstance> => {
    const { data } = await api.put<{ success: boolean; data: GitLabInstance }>(`/gitlab/instances/${id}`, updates);
    return data.data;
  },

  deleteInstance: async (id: string): Promise<void> => {
    await api.delete(`/gitlab/instances/${id}`);
  },

  testConnection: async (id: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>(`/gitlab/instances/${id}/test`);
    return data;
  },

  // Project browsing
  listProjects: async (instanceId: string, search?: string): Promise<GitLabProject[]> => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    const { data } = await api.get<GitLabProject[]>(`/gitlab/instances/${instanceId}/projects`, { params });
    return data;
  },

  browseTree: async (instanceId: string, projectId: number, path?: string, ref?: string): Promise<GitLabTreeItem[]> => {
    const params: Record<string, string> = {};
    if (path) params.path = path;
    if (ref) params.ref = ref;
    const { data } = await api.get<GitLabTreeItem[]>(
      `/gitlab/instances/${instanceId}/projects/${projectId}/tree`,
      { params },
    );
    return data;
  },

  getFile: async (instanceId: string, projectId: number, path: string, ref?: string): Promise<GitLabFileResult> => {
    const params: Record<string, string> = { path };
    if (ref) params.ref = ref;
    const { data } = await api.get<GitLabFileResult>(
      `/gitlab/instances/${instanceId}/projects/${projectId}/file`,
      { params },
    );
    return data;
  },

  listBranches: async (instanceId: string, projectId: number): Promise<GitLabBranch[]> => {
    const { data } = await api.get<GitLabBranch[]>(
      `/gitlab/instances/${instanceId}/projects/${projectId}/branches`,
    );
    return data;
  },

  listPipelines: async (instanceId: string, projectId: number): Promise<GitLabPipeline[]> => {
    const { data } = await api.get<GitLabPipeline[]>(
      `/gitlab/instances/${instanceId}/projects/${projectId}/pipelines`,
    );
    return data;
  },

  // Write operations (Phase 3b)
  createOrUpdateFile: async (
    instanceId: string,
    projectId: number,
    filePath: string,
    content: string,
    branch: string,
    commitMessage: string,
    action: 'create' | 'update' = 'update',
  ): Promise<void> => {
    await api.post(`/gitlab/instances/${instanceId}/projects/${projectId}/files`, {
      filePath, content, branch, commitMessage, action,
    });
  },

  createBranch: async (instanceId: string, projectId: number, branch: string, ref: string): Promise<void> => {
    await api.post(`/gitlab/instances/${instanceId}/projects/${projectId}/branches`, { branch, ref });
  },

  createMergeRequest: async (
    instanceId: string,
    projectId: number,
    sourceBranch: string,
    targetBranch: string,
    title: string,
  ): Promise<any> => {
    const { data } = await api.post(`/gitlab/instances/${instanceId}/projects/${projectId}/merge-requests`, {
      sourceBranch, targetBranch, title,
    });
    return data.data;
  },

  triggerPipeline: async (instanceId: string, projectId: number, ref: string, variables?: Array<{ key: string; value: string }>): Promise<any> => {
    const { data } = await api.post(`/gitlab/instances/${instanceId}/projects/${projectId}/pipelines`, {
      ref, variables,
    });
    return data.data;
  },
};
